import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import {
  TravelReminderTypeEnum,
  TravelReminderTypeEnumMapping
} from '../../../lib/types/enums/travel/travel-reminder-type.enum'
import { TripBookingReminderInput } from '../../../lib/types/models/trip-booking/trip-booking-reminder-input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedEnumOption } from '../../../lib/utils/functions/misc.functions'
import { createDateStringSchema } from '../../../lib/utils/validations/date.validation'
import { createEnumSchema } from '../../../lib/utils/validations/enum-validation'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledDateStringInput from '../../elements/inputs/controlled-date-string-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../elements/validation-buttons'

type Props = BaseFormProps<TripBookingReminderInput> & {
  className?: string
}

const formSchema: ZodType<TripBookingReminderInput> = z.object({
  type: createEnumSchema({ fieldName: 'Type de rappel', enumType: TravelReminderTypeEnum }),
  deadline: createDateStringSchema({ inputName: 'Date limite' }),
  daysBeforeDeadline: createNumberSchema('Jours avant la date limite'),
  comments: createStringSchema('Commentaire', { nullish: true })
})

const TripBookingReminderForm = ({
  className,
  defaultValues,
  ...validationButtonProps
}: Props) => {
  const form = useForm<TripBookingReminderInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex flex-col gap-3', className)}>
          <FormField
            control={form.control}
            name={'type'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledSelectInput<TravelReminderTypeEnum>
                    {...field}
                    required
                    options={Object.values(TravelReminderTypeEnum).map((type) => ({
                      label: TravelReminderTypeEnumMapping[type],
                      value: type
                    }))}
                    label={'Type de rappel'}
                    selectedOption={getSelectedEnumOption(
                      field.value,
                      TravelReminderTypeEnumMapping
                    )}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'deadline'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledDateStringInput
                    {...field}
                    required
                    mode="over"
                    disabledPastDates
                    label={'Date limite'}
                    iconIsRight
                    type={'appointment'}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'daysBeforeDeadline'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput
                    {...field}
                    label="Jours avant la date limite"
                    type="number"
                    isControllable
                    required
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'comments'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput {...field} label={'Commentaires'} type="textarea" />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <ValidationButtons hasBorderTop {...validationButtonProps} />
      </form>
    </Form>
  )
}

export default TripBookingReminderForm
